import type { DOMAttributes } from 'react';
import { useCallback } from 'react';

import type { BoardSwitcherSection } from './BoardSwitcher.types';
import { focusBoardSwitcherNavItem } from './focusBoardSwitcherNavItem';
import { keyboardNavSharedState } from './keyboardNavSharedState';

/**
 * Focus management should be disabled on board tile when a layer is opened
 * over a board tile, like a popover.
 */
let isFocusManagementDisabled = false;

export const disableFocusManagement = () => {
  isFocusManagementDisabled = true;
};

export const restoreFocusManagement = () => {
  isFocusManagementDisabled = false;
  const { activeId, activeSection } = keyboardNavSharedState.value;
  if (activeId) {
    focusBoardSwitcherNavItem(activeSection, activeId);
  }
};

const isBoardTileActive = (section: BoardSwitcherSection, boardId: string) => {
  const { activeId, activeSection } = keyboardNavSharedState.value;
  return activeId === boardId && activeSection === section;
};

const FOCUS_HIJACK_NODE_BLOCKLIST = ['INPUT', 'SELECT', 'TEXTAREA'];

/** When certain elements are focused, we shouldn't hijack focus on hover. */
const canHijackFocus = (): boolean => {
  const currentFocus = document.activeElement?.nodeName ?? '';
  return !FOCUS_HIJACK_NODE_BLOCKLIST.includes(currentFocus);
};

type HookResult = Pick<
  DOMAttributes<HTMLElement>,
  'onBlur' | 'onFocus' | 'onMouseLeave' | 'onMouseMove'
>;

/**
 * At any given time, one board tile can be "active", which means that it's
 * highlighted with a hovered background color and it becomes the target of
 * keyboard shortcuts that affect board tiles. Board tiles can become active
 * by either of two interactions: hover and arrow key navigation.
 * *
 * This hook is designed to combine all three modalities - hover, arrow keys,
 * and focus state - into a single deterministic state. Now, an "active" board
 * will always be focused, whether it had become active through hover, an arrow
 * key, or a tab cycle.
 */
export const useSetActiveBoardTileOnHoverOrFocus = (
  section: BoardSwitcherSection,
  boardId: string,
): HookResult => {
  const onFocus = useCallback(() => {
    if (!isBoardTileActive(section, boardId) && !isFocusManagementDisabled) {
      keyboardNavSharedState.setValue({
        activeId: boardId,
        activeSection: section,
      });
    }
  }, [boardId, section]);

  const onBlur = useCallback(() => {
    if (isBoardTileActive(section, boardId) && !isFocusManagementDisabled) {
      keyboardNavSharedState.setValue({
        activeId: null,
        activeSection: null,
      });
    }
  }, [boardId, section]);

  /**
   * Why are we listening to mousemove? Isn't mouseenter sufficient?
   *
   * 1. Hover your mouse over a board tile.
   * 2. Hit an arrow key, moving the selected board, but not in a way that
   *    scrolls the page -- so the mouse is still hovering over the original
   *    board tile.
   * 3. Move your mouse.
   *
   * If we were only listening to mouseenter, we'd never select the board,
   * because the mouse didn't re-enter it.
   */
  const onMouseMove = useCallback(() => {
    if (
      !isBoardTileActive(section, boardId) &&
      !isFocusManagementDisabled &&
      canHijackFocus()
    ) {
      // Hijack focus, even if it's anywhere else in the document. This
      // combines hover, focus, and active state to align on a single board.
      focusBoardSwitcherNavItem(section, boardId);
    }
  }, [boardId, section]);

  const onMouseLeave = useCallback(() => {
    if (isBoardTileActive(section, boardId) && !isFocusManagementDisabled) {
      (document.activeElement as HTMLElement)?.blur();
    }
  }, [boardId, section]);

  return {
    onFocus,
    onBlur,
    onMouseMove,
    onMouseLeave,
  };
};
